import { useMutation } from '@tanstack/react-query';
import axios from 'axios';
import { createApiCall } from 'services';
import type { ITransferGiftMetadata } from '@repo/types';

interface TransferCustomerInitData extends Pick<ITransferGiftMetadata, 'isAnonymous' | 'giftMessage'> {
  /**
   * VRG-358 — exactly one of these two addresses the transfer.
   *
   * `toCustomerEmail` is an address the sender TYPED (the invite path, which
   * works whether or not that address has an account). `toCustomerId` is an
   * `owners.id` for a recipient the client already RESOLVED — handle search, QR
   * scan, or a recent recipient. Those lookups deliberately return no email
   * (VRG-357 removed it, because returning it made the lookup an oracle), so
   * the id is the only way to address them.
   */
  toCustomerEmail?: string;
  toCustomerId?: string;
  type: 'gift' | 'sell';
  price?: number;
  currency?: string;
}

const transferCustomerInit = async (certificateId: string, data: TransferCustomerInitData) => {
  await createApiCall({
    method: 'POST',
    url: `/transfer/customer/init/${certificateId}`,
    data: data,
    throwError: true,
  });

  return { certificateId };
};

/**
 * VERA-304: this mutation deliberately does NOT invalidate on success. The
 * CALLER flushes, once the user has dismissed the confirmation.
 *
 * Why, with the evidence: the gift's "Gift sent" screen lives in a drawer stack
 * that is rendered by the certificate CARD (`CertificatesMenu` holds the
 * open state). Invalidating `['certificates']` here refetched the list the
 * instant the transfer was created, the card's data changed, and the whole
 * stack — the scan drawer AND the parent gift sheet — was torn down about
 * 950ms after the scan, before the confirmation could be read.
 *
 * That was measured, not inferred, by polling the DOM every 100ms through both
 * outcomes of the same flow:
 *   - mutation 400 (already mid-transfer, so this onSuccess never ran):
 *     "Gift sent" rendered and PERSISTED.
 *   - mutation 201 (gift actually created, so this onSuccess ran):
 *     scan drawer and gift sheet both gone in the SAME 100ms tick at ~954ms.
 * The only difference between the runs was whether this callback fired.
 *
 * Deferring the flush costs nothing: the only stale surface is the list sitting
 * BEHIND a full-screen confirmation the user is currently reading, and the
 * caller invalidates the moment they leave it (Done, or View certificate).
 */
export const useTransferCustomerInit = () => {
  return useMutation({
    mutationFn: ({ certificateId, data }: { certificateId: string; data: TransferCustomerInitData }) => {
      return transferCustomerInit(certificateId, data);
    },
  });
};
